import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { forkJoin } from 'rxjs';

import { BaseComponent } from '../../../core/abstractions/base.component';
import { CurrentShareholderGeneralMeetingModel } from '../../../models/current-shareholder-general-meeting.model';
import { RegistrationStatusEnum } from '../../../models/enums/registration-status.enum';
import { GeneralMeetingModel } from '../../../models/general-meeting.model';
import { ShareholderInstructionsSummaryModel } from '../../../models/shareholder-instructions-summary.model';
import { VotingInstructionsModel } from '../../../models/voting-instructions.model';
import { BasicDataService } from '../../../services/basic-data.service';
import { GeneralMeetingCheckInService } from '../../../services/general-meeting-check-in.service';
import { GeneralMeetingsResource } from '../../../services/resources/general-meetings.resource';
import { TranslationService } from '../../../services/translation.service';
import { AgendaItemsComponent } from '../../dialogs/agenda-items/agenda-items.component';
import { SelfAttendanceDialog } from './self-attendance/self-attendance.dialog';

@Component({
    selector: 'sl-general-meeting-registration',
    templateUrl: './general-meeting-registration.component.html',
    styleUrls: ['./general-meeting-registration.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class GeneralMeetingRegistrationComponent extends BaseComponent implements OnInit {
    RegistrationStatusEnum = RegistrationStatusEnum;
    currentMeeting: GeneralMeetingModel;
    currentShareholderData: CurrentShareholderGeneralMeetingModel;
    registrationStatus = RegistrationStatusEnum.NoAnswer;
    shouldAllowWebRegistrationSelf = false;
    shouldAllowWebRegistrationIndependentProxy = false;
    shouldAllowWebAttendance = false;
    shouldAllowWebInstructionsChange = false;
    instructions?: ShareholderInstructionsSummaryModel;
    statusDescription = '';
    instructionsButtonText = '';
    isLoading = true;
    isSendingAdmissionCardEmail = false;

    private isCheckedIn = false;
    private shouldNetvoteUseWordingForCooperatives = false;

    private readonly matDialog = inject(MatDialog);
    private readonly generalMeetingsResource = inject(GeneralMeetingsResource);
    private readonly basicDataService = inject(BasicDataService);
    private readonly generalMeetingCheckinService = inject(GeneralMeetingCheckInService);
    private readonly translationService = inject(TranslationService);

    ngOnInit(): void {
        const checkInStatusSub = this.generalMeetingCheckinService.checkInStatusChange().subscribe(isCheckedIn => {
            this.isCheckedIn = isCheckedIn;
            this.fetchData();
        });
        this.addSubscriptions(checkInStatusSub);

        this.fetchData();
    }

    onSelfAttend(): void {
        if (this.registrationStatus === RegistrationStatusEnum.Attending) {
            return;
        }

        const dialogRef = this.matDialog.open(SelfAttendanceDialog, {
            width: '350px',
            height: '250px',
            maxHeight: '98svh',
            maxWidth: '98vw'
        });

        dialogRef.afterClosed().subscribe(hasChanges => {
            if (hasChanges) {
                this.fetchData();
            }
        });
    }

    onGiveInstructions(): void {
        let isEditing = false;

        if (
            this.registrationStatus === RegistrationStatusEnum.Representative
            || this.registrationStatus === RegistrationStatusEnum.IndependentRepresentative
        ) {
            isEditing = true;
        }

        const ref = this.matDialog.open(AgendaItemsComponent, {
            minWidth: '65%',
            minHeight: '40%',
            panelClass: 'full-width-dialog',
            disableClose: true,
            data: new VotingInstructionsModel({
                shareholderData: this.currentShareholderData,
                generalMeeting: this.currentMeeting,
                isEditing
            })
        });

        ref.afterClosed().subscribe(() =>
            this.generalMeetingsResource.getCurrentForShareholder().subscribe(r => {
                this.currentShareholderData = r.value;
                this.registrationStatus = r.value.registrationStatus;

                this.generalMeetingsResource.getCurrentInstructionsForShareholder().subscribe(ri => (this.instructions = ri.value));

                this.setStatusDescription();
                this.setInstructionsButtonText();
            })
        );
    }

    getTemplateName(): string {
        const currentTime = new Date(new Date().getTime());

        if (this.currentMeeting && currentTime < this.currentMeeting.doorOpening) {
            return 'instructionsTempl';
        }

        return this.currentShareholderData && this.isCheckedIn ? 'checkOutTempl' : 'checkInTempl';
    }

    setStatusDescription(): void {
        const result = this.currentShareholderData.registrationStatusDescription;
        this.statusDescription = result;

        if (this.shouldNetvoteUseWordingForCooperatives) {
            // replace the status description for the following cases
            if (this.registrationStatus === RegistrationStatusEnum.NoAnswer) {
                this.translationService
                    .getText('GeneralMeeting.StatusGmRegistrationStatusNoAnswerCooperative')
                    .subscribe(r => (this.statusDescription = r));
            } else if (this.registrationStatus === RegistrationStatusEnum.IndependentRepresentative) {
                this.translationService
                    .getText('GeneralMeeting.StatusGmRegistrationStatusIndependentRepresentativeCooperative')
                    .subscribe(r => (this.statusDescription = r));
            }
        }
    }

    setInstructionsButtonText(): void {
        if (!this.shouldNetvoteUseWordingForCooperatives) {
            if (
                this.registrationStatus === RegistrationStatusEnum.Representative
                || this.registrationStatus === RegistrationStatusEnum.IndependentRepresentative
            ) {
                this.translationService.getText('GeneralMeeting.ChangeInstructions').subscribe(r => (this.instructionsButtonText = r));
            } else {
                this.translationService.getText('GeneralMeeting.GiveInstructions').subscribe(r => (this.instructionsButtonText = r));
            }
        } else {
            if (
                this.registrationStatus === RegistrationStatusEnum.Representative
                || this.registrationStatus === RegistrationStatusEnum.IndependentRepresentative
            ) {
                this.translationService
                    .getText('GeneralMeeting.ChangeInstructionsCooperative')
                    .subscribe(r => (this.instructionsButtonText = r));
            } else {
                this.translationService
                    .getText('GeneralMeeting.GiveInstructionsCooperative')
                    .subscribe(r => (this.instructionsButtonText = r));
            }
        }
    }

    onCheckIn(): void {
        this.generalMeetingCheckinService.checkIn();
    }

    onCheckOut(): void {
        this.generalMeetingCheckinService.checkOut();
    }

    resendAdmissionCardEmail(): void {
        this.isSendingAdmissionCardEmail = true;
        this.generalMeetingsResource
            .resendGmAdmissionCard()
            .subscribe()
            .add(() => (this.isSendingAdmissionCardEmail = false));
    }

    private fetchData(): void {
        this.isLoading = true;
        this.instructions = undefined;

        const requests = forkJoin({
            basicData: this.basicDataService.get(),
            currentMeetingResponse: this.generalMeetingsResource.getCurrent(),
            currentShareholderResponse: this.generalMeetingsResource.getCurrentForShareholder()
        });

        requests.subscribe(r => {
            this.shouldAllowWebRegistrationSelf = r.basicData.allowWebRegistrationSelf ?? false;
            this.shouldAllowWebRegistrationIndependentProxy = r.basicData.allowWebRegistrationIndependentProxy ?? false;
            this.shouldAllowWebAttendance = r.basicData.allowWebAttendance ?? false;
            this.shouldNetvoteUseWordingForCooperatives = r.basicData.netvoteUseWordingForCooperatives;
            this.currentMeeting = r.currentMeetingResponse.value;
            const now = new Date().getTime();
            this.shouldAllowWebInstructionsChange = now < r.currentMeetingResponse.value.webInstructionsToDate.getTime();

            this.currentShareholderData = r.currentShareholderResponse.value;
            this.isCheckedIn = this.currentShareholderData?.isCheckedIn ?? false;
            this.registrationStatus = this.currentShareholderData.registrationStatus;

            if (this.registrationStatus === RegistrationStatusEnum.IndependentRepresentative) {
                this.generalMeetingsResource.getCurrentInstructionsForShareholder().subscribe(ri => (this.instructions = ri.value));
            }

            this.setStatusDescription();
            this.setInstructionsButtonText();
            this.isLoading = false;
        });

        this.generalMeetingsResource.getCurrentInstructionsForShareholder().subscribe(r => (this.instructions = r.value));
    }
}
